import React, { useState } from "react";
import { Icon } from "@iconify-icon/react";

export type IconCircleButtonVariant = "default" | "delete" | "danger";
export type IconCircleButtonContext = "education" | "work";

interface IconCircleButtonProps {
  label: string;
  icon: string;
  variant?: IconCircleButtonVariant;
  onClick?: () => void;
}

const IconCircleButton: React.FC<IconCircleButtonProps> = ({
  label,
  icon,

  variant = "default",
  onClick,
}) => {
  const [hovered, setHovered] = useState(false);

  const base: React.CSSProperties = {
    width: 36,
    height: 36,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    border: "1px solid",
    cursor: "pointer",
    transition: "all 120ms ease",
    outline: "none",
    boxShadow: hovered ? "0 2px 6px rgba(0,0,0,0.12)" : "none",
    transform: hovered ? "translateY(-1px)" : "none",
    userSelect: "none",
  };

  const isDanger = variant === "delete" || variant === "danger";

  // Preserve slightly different palettes used in Education vs Work history
  const palette = (() => {
    if (isDanger) {
      return {
        bg: hovered ? "#f3b7be" : "#f5c6cb",
        color: "#a4262c",
        border: hovered ? "#fde8ea" : "#f9ecef",
      };
    }
    return {
      bg: hovered ? "#c6d9ed" : "#d8e1ed",
      color: "#00345D",
      border: hovered ? "#e9f2fb" : "#F2F7FB",
    };
  })();

  const style: React.CSSProperties = {
    ...base,
    background: palette.bg,
    color: palette.color,
    borderColor: palette.border,
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (onClick) {
        onClick();
      }
    }
  };

  return (
    <button
      aria-label={label}
      title={label}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      onKeyDown={onKeyDown}
      style={style}
    >
      <Icon icon={icon} width={18} height={18} />
    </button>
  );
};

export default IconCircleButton;
